import { Button, Empty } from "antd";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import { useAdvanceTable } from "hooks";
import useCredits from "hooks/donations/useCredits";
import useFormattedDate from "hooks/useFormatDate";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

const STATUS_STYLES = {
  ACTIVE: {
    bg: "#E8F7E3",
    text: "#2E7D32",
    border: "#D5EBD0",
    label: "Active",
  },
  REDEEMED: {
    bg: "#E3F2FD",
    text: "#1565C0",
    border: "#BBDEFB",
    label: "Redeemed",
  },
  EXPIRED: {
    bg: "#FFF4E5",
    text: "#ED6C02",
    border: "#FFE0B2",
    label: "Expired",
  },
  DELETED: {
    bg: "#FDECEA",
    text: "#C62828",
    border: "#F5C6C3",
    label: "Deleted",
  },
};

export const CreditsTable = () => {
  const navigate = useNavigate();
  const { credits, loading, error } = useCredits();

  const { formatDateTime } = useFormattedDate();
  const formatID = (id) => `${id.slice(0, 5)}...`;
  const formatText = (text) => `${text.slice(0, 20)}...`;

  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ getValue }) => formatID(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "code",
      header: "Code",
      cell: ({ getValue }) => {
        const code = getValue();
        if (!code) return "NULL";
        return code;
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "donation",
      header: "Donor",
      cell: ({ getValue }) => {
        const donation = getValue();
        if (!donation.donor) return "NULL";

        // Merge firstName + lastName
        const fullName = `${donation.donor.firstName || ""} ${
          donation.donor.lastName || ""
        }`.trim();
        return fullName || donation.donor.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "donee",
      header: "Donee",
      cell: ({ getValue }) => {
        const donee = getValue();
        if (!donee) return "NULL";

        // Merge firstName + lastName
        const fullName = `${donee.firstName || ""} ${
          donee.lastName || ""
        }`.trim();
        return fullName || donee.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "unit",
      header: "Unit",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "amount",
      header: "Amount (USD)",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "balance",
      header: "Balance (USD)",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const status = STATUS_STYLES[value] || {
          bg: "#F3F4F6",
          text: "#374151",
          border: "#E5E7EB",
          label: value || "Unknown",
        };
        return (
          <span
            className="py-2"
            style={{
              backgroundColor: status.bg,
              color: status.text,
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "0.65rem",
              border: `1px solid ${status.border}`,
              cursor: "default",
              fontWeight: 600,
            }}
          >
            {status.label}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "issuedAt",
      header: "Issued At",
      cell: ({ getValue }) => {
        return formatDateTime(getValue());
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <Guard privilege={PRIV.VIEW_CREDITS}>
            <Button
              className="text-xxs"
              size="sm"
              onClick={() => navigate(`/olitask/credits/${item.id}`)}
            >
              View
            </Button>
          </Guard>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: credits,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="dollar-sign"
        title="Credits"
        subtitle="Manage and monitor all issued donation credits"
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      {credits && credits.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <Row className="mb-2">
            <Col md={4}>
              <AdvanceTableSearchBox />
            </Col>
          </Row>

          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle"
            rowClassName="btn-reveal-trigger text-nowrap align-middle"
            tableProps={{
              bordered: true,
              striped: true,
              className: "text-xxs fw-normal mb-0 overflow-hidden",
            }}
          />

          <div className="mt-3">
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </div>
        </AdvanceTableProvider>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 200 }}
        >
          <Empty description="No credits data available" />
        </div>
      )}
    </>
  );
};
